import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useApi } from '../hooks/useApi';
import { toast } from 'react-hot-toast';
import { pillClass } from '../utils/filterPill';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];
const GCP_COLORS = ['#9333EA', '#3B82F6', '#10B981', '#F59E0B', '#EC4899', '#6366F1'];

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white p-3 rounded-lg shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1)] border-none">
                {label && <p className="font-semibold text-gray-700 mb-2">{label}</p>}
                <div className="space-y-2">
                    {payload.map((entry, index) => {
                        const color = entry.color || (entry.payload && entry.payload.fill) || '#8884d8';
                        return (
                            <div key={`item-${index}`} className="flex items-center text-sm gap-2">
                                <span 
                                    className="px-2 py-0.5 rounded text-white text-xs font-medium"
                                    style={{ backgroundColor: color }}
                                >
                                    {entry.name}
                                </span>
                                <span style={{ color: color }} className="font-medium">
                                    {entry.value}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
    return null;
};

const toIsoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// The current month is open-ended (end = '' → "Today"), so it matches the page's default range.
const monthRange = (year, month) => {
    const now = new Date();
    const isCurrent = year === now.getFullYear() && month === now.getMonth();
    return { start: toIsoDate(new Date(year, month, 1)), end: isCurrent ? '' : toIsoDate(new Date(year, month + 1, 0)) };
};

const AdminOrdersDashboardSkeleton = () => {
    return (
        <div className="space-y-8 animate-pulse">
            {/* Current Month Revenue Overview Card Skeleton */}
            <div className="bg-gradient-to-r from-primary/20 to-[#8b236a]/20 border border-primary/10 rounded-lg p-6 flex items-center justify-between h-28">
                <div className="space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-48"></div>
                    <div className="h-8 bg-gray-200 rounded w-36"></div>
                </div>
                <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
            </div>

            {/* Top 3 Charts Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                    <div key={i} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 h-96 flex flex-col justify-between">
                        <div className="h-5 bg-gray-200 rounded w-48"></div>
                        <div className="flex-grow flex items-end justify-around pt-8 pb-4">
                            {[40, 60, 30, 80, 50, 70].map((h, idx) => (
                                <div key={idx} className="bg-gray-200 rounded-t w-10" style={{ height: `${h}%` }}></div>
                            ))}
                        </div>
                        <div className="h-4 bg-gray-100 rounded w-full"></div>
                    </div>
                ))}
            </div>

            {/* Remaining Charts Grid Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Top Customers Skeleton */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 h-96 flex flex-col justify-between">
                    <div className="h-5 bg-gray-200 rounded w-56"></div>
                    <div className="flex-grow flex flex-col justify-center space-y-4 py-4">
                        {[80, 65, 55, 40].map((w, i) => (
                            <div key={i} className="flex items-center gap-4">
                                <div className="h-4 bg-gray-200 rounded w-24"></div>
                                <div className="h-6 bg-gray-200 rounded" style={{ width: `${w}%` }}></div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Top Cart Items Skeleton */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 h-96 flex flex-col justify-between">
                    <div className="h-5 bg-gray-200 rounded w-56"></div>
                    <div className="flex-grow flex flex-col justify-center space-y-4 py-4">
                        {[75, 60, 50, 35].map((w, i) => (
                            <div key={i} className="flex items-center gap-4">
                                <div className="h-4 bg-gray-200 rounded w-32"></div>
                                <div className="h-6 bg-gray-200 rounded" style={{ width: `${w}%` }}></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const AdminOrdersDashboard = ({ googleIdToken, t, language }) => {
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedData, setSelectedData] = useState(null);
    const [modalTitle, setModalTitle] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [startDate, setStartDate] = useState(() => {
        const now = new Date();
        return monthRange(now.getFullYear(), now.getMonth()).start;
    });
    const [endDate, setEndDate] = useState('');
    const [paymentType, setPaymentType] = useState('all');
    const [, setCurrentMonthRevenue] = useState(0);

    const locale = language === 'en' ? 'en-US' : 'nl-NL';
    const monthPills = useMemo(() => {
        const now = new Date();
        return Array.from({ length: now.getMonth() + 1 }, (_, m) => ({
            ...monthRange(now.getFullYear(), m),
            month: m,
            label: new Date(now.getFullYear(), m, 1).toLocaleString(locale, { month: 'short' }),
            longLabel: new Date(now.getFullYear(), m, 1).toLocaleString(locale, { month: 'long', year: 'numeric' }),
        }));
    }, [locale]);
    const activePill = monthPills.find(p => p.start === startDate && p.end === endDate);
    const isCurrentMonthSelected = activePill && activePill.month === new Date().getMonth();

    const { apiRequest } = useApi(googleIdToken, () => window.location.reload());

    const handleChartClick = (data, type) => {
        const payload = data && data.payload ? data.payload : data;
        if (!payload || !payload.orders || payload.orders.length === 0) return;
        setModalTitle(`${type}: ${payload.name}`);
        setSelectedData(payload.orders);
        setIsModalOpen(true);
    };

    useEffect(() => {
        let cancelled = false;
        const fetchOrders = async () => {
            setIsLoading(true);
            try {
                let url = `/api/admin/orders?limit=10000`;
                if (startDate) url += `&startDate=${startDate}`;
                if (endDate) url += `&endDate=${endDate}`;
                const response = await apiRequest(url);
                if (!cancelled && response && response.orders) {
                    setOrders(response.orders);
                    setCurrentMonthRevenue(response.currentMonthRevenue || 0);
                }
            } catch (error) {
                if (cancelled) return;
                console.error("Failed to fetch orders:", error);
                toast.error("Failed to load orders data.");
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        if (googleIdToken) {
            fetchOrders();
        }
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [googleIdToken, startDate, endDate]);

    const { monthlyRevenue, customerPayments, paymentMethods, cartItemStats, flexItemStats, flexMonthlyDomainStats, topFlexDomains, gcpMonthlyDomainStats, topGcpDomains, hasGcpData, totalRevenue } = useMemo(() => {
        const monthlyRevMap = new Map();
        const customerMap = new Map();
        const paymentMethodMap = new Map();
        const cartItemMap = new Map();
        const flexItemMap = new Map();
        const flexMonthlyDomainMap = new Map();
        const gcpMonthlyDomainMap = new Map();
        let totalRevenueSum = 0;

        const filteredOrders = orders.filter(order => {
            const isPostpaid = (order.paymentMethod || '').toLowerCase() === 'postpaid';
            if (paymentType === 'postpaid' && !isPostpaid) return false;
            if (paymentType === 'prepaid' && isPostpaid) return false;
            if (!searchTerm) return true;
            const searchLower = searchTerm.toLowerCase().trim();
            const customerStr = (order.customerName || order.customerDomain || order.debtorName || order.customerId || order.debtorId || 'Unknown').toLowerCase();
            const orderIdStr = (order.id || '').toLowerCase();
            return customerStr.includes(searchLower) || orderIdStr.includes(searchLower);
        });

        filteredOrders.forEach(order => {
            totalRevenueSum += (order.totalPrice || 0);

            if (order.createdAt) {
                const date = new Date(order.createdAt);
                const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                if (!monthlyRevMap.has(monthYear)) {
                    monthlyRevMap.set(monthYear, { renewalsRevenue: 0, extraRevenue: 0, flexRevenue: 0, gcpRevenue: 0, orders: [] });
                }
                if (!flexMonthlyDomainMap.has(monthYear)) {
                    flexMonthlyDomainMap.set(monthYear, new Map());
                }
                if (!gcpMonthlyDomainMap.has(monthYear)) {
                    gcpMonthlyDomainMap.set(monthYear, new Map());
                }
                const monthData = monthlyRevMap.get(monthYear);
                const monthDomainMap = flexMonthlyDomainMap.get(monthYear);
                const gcpMonthDomainMap = gcpMonthlyDomainMap.get(monthYear);

                let renewalSubtotal = 0;
                let extraSubtotal = 0;
                let flexSubtotal = 0;
                let gcpSubtotal = 0;

                (order.cartItems || []).forEach(item => {
                    const isRenewal = item.isRenewal === true || item.isRenewal === 'true';
                    const isFlex = item.isFlexiblePlan === true || item.isFlexiblePlan === 'true' || order.isFlexiblePlanInvoice === true;
                    const isGcp = item.isGcpPlan === true || item.isGcpPlan === 'true' || item.isGcp === true || order.isGcpInvoice === true || order.orderType === 'GCP' || (item.name && item.name.toLowerCase().includes('gcp')) || item.afas_artikelcode === 'GCPCOST';

                    let linePrice = 0;
                    if ((isFlex || isGcp) && item.totalPrice !== undefined) {
                        linePrice = parseFloat(item.totalPrice) || 0;
                    } else {
                        linePrice = (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 1);
                    }

                    if (isGcp) {
                        gcpSubtotal += linePrice;
                    } else if (isFlex) {
                        flexSubtotal += linePrice;

                        const name = item.name || item.description || 'Unknown Item';
                        if (!flexItemMap.has(name)) {
                            flexItemMap.set(name, { count: 0, revenue: 0, orders: [] });
                        }
                        const flexData = flexItemMap.get(name);
                        flexData.count += (parseFloat(item.quantity) || 1);
                        flexData.revenue += linePrice;
                        flexData.orders.push(order);

                    } else if (isRenewal) {
                        renewalSubtotal += linePrice;
                    } else {
                        extraSubtotal += linePrice;
                    }
                });

                const totalSubtotal = renewalSubtotal + extraSubtotal + flexSubtotal + gcpSubtotal;
                let orderRenewalRev = 0;
                let orderExtraRev = 0;
                let orderFlexRev = 0;
                let orderGcpRev = 0;

                if (totalSubtotal !== 0) {
                    const renewalRatio = renewalSubtotal / totalSubtotal;
                    const flexRatio = flexSubtotal / totalSubtotal;
                    const gcpRatio = gcpSubtotal / totalSubtotal;
                    orderRenewalRev = (order.totalPrice || 0) * renewalRatio;
                    orderFlexRev = (order.totalPrice || 0) * flexRatio;
                    orderGcpRev = (order.totalPrice || 0) * gcpRatio;
                    orderExtraRev = (order.totalPrice || 0) * (1 - renewalRatio - flexRatio - gcpRatio);
                } else {
                    if (order.isGcpInvoice === true || order.orderType === 'GCP') {
                        orderGcpRev = (order.totalPrice || 0);
                    } else {
                        orderExtraRev = (order.totalPrice || 0);
                    }
                }

                monthData.renewalsRevenue += orderRenewalRev;
                monthData.extraRevenue += orderExtraRev;
                monthData.flexRevenue += orderFlexRev;
                monthData.gcpRevenue += orderGcpRev;
                monthData.orders.push(order);

                if (orderFlexRev !== 0) {
                    const domain = order.customerDomain || order.customerName || order.debtorName || order.customerId || 'Unknown';
                    monthDomainMap.set(domain, (monthDomainMap.get(domain) || 0) + orderFlexRev);
                }

                if (orderGcpRev !== 0) {
                    const domain = order.customerDomain || order.customerName || order.debtorName || order.customerId || 'Unknown';
                    gcpMonthDomainMap.set(domain, (gcpMonthDomainMap.get(domain) || 0) + orderGcpRev);
                }
            }

            const customerName = order.customerName || order.customerDomain || order.debtorName || order.customerId || order.debtorId || 'Unknown';
            if (!customerMap.has(customerName)) {
                customerMap.set(customerName, { revenue: 0, orders: [] });
            }
            const custData = customerMap.get(customerName);
            custData.revenue += (order.totalPrice || 0);
            custData.orders.push(order);

            const method = order.paymentMethod || 'Unknown';
            if (!paymentMethodMap.has(method)) {
                paymentMethodMap.set(method, { count: 0, orders: [] });
            }
            const methodData = paymentMethodMap.get(method);
            methodData.count += 1;
            methodData.orders.push(order);

            (order.cartItems || []).forEach(item => {
                const name = item.name || item.description || 'Unknown Item';
                if (!cartItemMap.has(name)) {
                    cartItemMap.set(name, { count: 0, revenue: 0, orders: [] });
                }
                const itemData = cartItemMap.get(name);
                itemData.count += (item.quantity || 1);
                itemData.revenue += ((item.price || 0) * (item.quantity || 1));
                itemData.orders.push(order);
            });
        });

        const sortedMonths = Array.from(monthlyRevMap.keys()).sort();
        const formattedMonthlyRevenue = sortedMonths.map(month => ({
            name: month,
            Renewals: parseFloat(monthlyRevMap.get(month).renewalsRevenue.toFixed(2)),
            'Extra Orders': parseFloat(monthlyRevMap.get(month).extraRevenue.toFixed(2)),
            'Flex Plans': parseFloat(monthlyRevMap.get(month).flexRevenue.toFixed(2)),
            GCP: parseFloat((monthlyRevMap.get(month).gcpRevenue || 0).toFixed(2)),
            orders: monthlyRevMap.get(month).orders
        }));

        const formattedCustomerPayments = Array.from(customerMap.entries())
            .map(([name, data]) => ({ name, value: parseFloat(data.revenue.toFixed(2)), orders: data.orders }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 10);

        const formattedPaymentMethods = Array.from(paymentMethodMap.entries())
            .map(([name, data]) => ({ name, value: data.count, orders: data.orders }));

        const formattedCartItems = Array.from(cartItemMap.entries())
            .map(([name, data]) => ({ name, count: data.count, revenue: parseFloat(data.revenue.toFixed(2)), orders: data.orders }))
            .sort((a, b) => b.revenue - a.revenue)
            .slice(0, 10);

        const formattedFlexItems = Array.from(flexItemMap.entries())
            .map(([name, data]) => ({ name, count: data.count, revenue: parseFloat(data.revenue.toFixed(2)), orders: data.orders }))
            .sort((a, b) => b.revenue - a.revenue)
            .slice(0, 10);

        const domainFlexTotal = new Map();
        for (const monthMap of flexMonthlyDomainMap.values()) {
            for (const [domain, rev] of monthMap.entries()) {
                domainFlexTotal.set(domain, (domainFlexTotal.get(domain) || 0) + rev);
            }
        }
        const topFlexDomains = Array.from(domainFlexTotal.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(e => e[0]);

        const formattedFlexMonthlyDomainStats = sortedMonths.map(month => {
            const data = { name: month };
            const monthMap = flexMonthlyDomainMap.get(month) || new Map();
            let otherRev = 0;
            for (const [domain, rev] of monthMap.entries()) {
                if (topFlexDomains.includes(domain)) {
                    data[domain] = parseFloat(rev.toFixed(2));
                } else {
                    otherRev += rev;
                }
            }
            if (otherRev > 0) {
                data['Other'] = parseFloat(otherRev.toFixed(2));
            }
            return data;
        });

        const domainGcpTotal = new Map();
        for (const monthMap of gcpMonthlyDomainMap.values()) {
            for (const [domain, rev] of monthMap.entries()) {
                domainGcpTotal.set(domain, (domainGcpTotal.get(domain) || 0) + rev);
            }
        }
        const topGcpDomains = Array.from(domainGcpTotal.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(e => e[0]);

        const formattedGcpMonthlyDomainStats = sortedMonths.map(month => {
            const data = {
                name: month,
                orders: (monthlyRevMap.get(month)?.orders || []).filter(o => o.isGcpInvoice || o.orderType === 'GCP' || (o.cartItems || []).some(ci => ci.isGcpPlan || ci.afas_artikelcode === 'GCPCOST'))
            };
            const monthMap = gcpMonthlyDomainMap.get(month) || new Map();
            let otherRev = 0;
            for (const [domain, rev] of monthMap.entries()) {
                if (topGcpDomains.includes(domain)) {
                    data[domain] = parseFloat(rev.toFixed(2));
                } else {
                    otherRev += rev;
                }
            }
            if (otherRev > 0) {
                data['Other'] = parseFloat(otherRev.toFixed(2));
            }
            return data;
        });

        const hasGcpData = topGcpDomains.length > 0 || formattedGcpMonthlyDomainStats.some(s => (s.Other || 0) > 0);

        return {
            monthlyRevenue: formattedMonthlyRevenue,
            customerPayments: formattedCustomerPayments,
            paymentMethods: formattedPaymentMethods,
            cartItemStats: formattedCartItems,
            flexItemStats: formattedFlexItems,
            flexMonthlyDomainStats: formattedFlexMonthlyDomainStats,
            topFlexDomains: topFlexDomains,
            gcpMonthlyDomainStats: formattedGcpMonthlyDomainStats,
            topGcpDomains: topGcpDomains,
            hasGcpData: hasGcpData,
            totalRevenue: totalRevenueSum
        };
    }, [orders, searchTerm, paymentType]);

    const selectMonth = (pill) => {
        setStartDate(pill.start);
        setEndDate(pill.end);
    };

    let revenueTitle = t?.revenue || 'Revenue';
    let revenueRange = `(${startDate || t?.range_start || 'Start'} - ${endDate || t?.range_today || 'Today'})`;
    if (isCurrentMonthSelected) {
        revenueTitle = t?.current_month_revenue || 'Current Month Revenue';
        revenueRange = `(${activePill.longLabel})`;
    } else if (activePill) {
        revenueTitle = `${revenueTitle} ${activePill.longLabel}`;
        revenueRange = '';
    }

    return (
        <div className="p-4 sm:p-8 space-y-8">
            <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center mb-6 gap-4">
                <h1 className="text-3xl font-bold text-gray-800 flex items-center shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-3 bg-primary text-white rounded-full p-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {t?.admin_orders_dashboard || 'Admin Orders Dashboard'}
                </h1>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full xl:w-auto">
                    {/* Date Filters */}
                    <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 bg-white w-full sm:w-auto overflow-hidden">
                        <svg className="h-5 w-5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full sm:w-32 py-2 focus:outline-none text-sm text-gray-600 bg-transparent"
                            title="Start Date"
                        />
                        <span className="text-gray-300">-</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full sm:w-32 py-2 focus:outline-none text-sm text-gray-600 bg-transparent"
                            title="End Date"
                        />
                    </div>

                    <div className="relative w-full sm:w-64">
                        <input
                            type="text"
                            placeholder={t?.search_customer || 'Search customer...'}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                        <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                    </div>
                </div>
            </div>

            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
                <div className="flex flex-wrap gap-2">
                    {monthPills.map(pill => (
                        <button key={pill.month} onClick={() => selectMonth(pill)} className={`${pillClass(activePill === pill)} capitalize`}>
                            {pill.label}
                        </button>
                    ))}
                </div>
                <div className="flex flex-wrap gap-2">
                    {[
                        ['all', t?.filter_all || 'All'],
                        ['prepaid', t?.prepaid || 'Prepaid'],
                        ['postpaid', t?.postpaid || 'Postpaid'],
                    ].map(([value, label]) => (
                        <button key={value} onClick={() => setPaymentType(value)} className={pillClass(paymentType === value)}>
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {isLoading ? <AdminOrdersDashboardSkeleton /> : (<>
            {/* Current Month Revenue Overview Card */}
            <div className="bg-gradient-to-r from-primary to-[#8b236a] rounded-lg shadow-md p-6 text-white flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-medium opacity-90 flex items-center gap-2">
                        {revenueTitle}
                        {revenueRange && (
                            <span className="text-base opacity-80 font-normal">{revenueRange}</span>
                        )}
                    </h2>
                    <p className="text-4xl font-bold mt-2">
                        €{totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                </div>
                <div className="p-3 bg-white/20 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 15.536c-1.171 1.952-3.07 1.952-4.242 0-1.172-1.953-1.172-5.119 0-7.072 1.171-1.952 3.07-1.952 4.242 0M8 10.5h4m-4 3h4m9-1.5a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
            </div>

            {/* Top Row: Charts (Monthly Revenue, Flex Plan by Domain, GCP by Domain if data available) */}
            <div className={`grid grid-cols-1 ${hasGcpData ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-6`}>
                {/* Monthly Revenue Chart */}
                <div className="bg-white p-6 rounded-lg shadow-sm w-full">
                    <h2 className="text-xl font-semibold mb-6 text-gray-700">{t?.monthly_revenue || 'Monthly Revenue (€)'}</h2>
                    <div className="h-96">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={monthlyRevenue} margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                <XAxis dataKey="name" tick={{ fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                                <YAxis tick={{ fill: '#6B7280' }} axisLine={false} tickLine={false} tickFormatter={(val) => `€${val}`} />
                                <RechartsTooltip cursor={{ fill: '#F3F4F6' }} content={<CustomTooltip />} />
                                <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ paddingLeft: '10px' }} />
                                <Bar dataKey="Renewals" fill="#0F9D58" radius={[4, 4, 0, 0]} barSize={20} onClick={(data) => handleChartClick(data, t?.monthly_revenue || 'Monthly Revenue')} className="cursor-pointer hover:opacity-80 transition-opacity" />
                                <Bar dataKey="Extra Orders" fill="#1A73E8" radius={[4, 4, 0, 0]} barSize={20} onClick={(data) => handleChartClick(data, t?.monthly_revenue || 'Monthly Revenue')} className="cursor-pointer hover:opacity-80 transition-opacity" />
                                <Bar dataKey="Flex Plans" fill="#FFBB28" radius={[4, 4, 0, 0]} barSize={20} onClick={(data) => handleChartClick(data, t?.monthly_revenue || 'Monthly Revenue')} className="cursor-pointer hover:opacity-80 transition-opacity" />
                                <Bar dataKey="GCP" fill="#9333EA" radius={[4, 4, 0, 0]} barSize={20} onClick={(data) => handleChartClick(data, t?.monthly_revenue || 'Monthly Revenue')} className="cursor-pointer hover:opacity-80 transition-opacity" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Monthly Flex Plans by Domain Chart */}
                <div className="bg-white p-6 rounded-lg shadow-sm w-full">
                    <h2 className="text-xl font-semibold mb-6 text-gray-700">{t?.monthly_flex_domain_revenue || 'Flex Plan Revenue by Domain (€)'}</h2>
                    <div className="h-96">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={flexMonthlyDomainStats} margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                <XAxis dataKey="name" tick={{ fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                                <YAxis tick={{ fill: '#6B7280' }} axisLine={false} tickLine={false} tickFormatter={(val) => `€${val}`} />
                                <RechartsTooltip cursor={{ fill: '#F3F4F6' }} content={<CustomTooltip />} />
                                <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ paddingLeft: '10px' }} />
                                {topFlexDomains.map((domain, index) => (
                                    <Bar key={domain} dataKey={domain} fill={COLORS[index % COLORS.length]} radius={[4, 4, 0, 0]} onClick={(data) => handleChartClick(data, t?.monthly_flex_domain_revenue || 'Flex Plan Revenue by Domain')} className="cursor-pointer hover:opacity-80 transition-opacity" />
                                ))}
                                <Bar dataKey="Other" fill="#D1D5DB" radius={[4, 4, 0, 0]} onClick={(data) => handleChartClick(data, t?.monthly_flex_domain_revenue || 'Flex Plan Revenue by Domain')} className="cursor-pointer hover:opacity-80 transition-opacity" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Monthly GCP by Domain Chart */}
                {hasGcpData && (
                    <div className="bg-white p-6 rounded-lg shadow-sm w-full">
                        <h2 className="text-xl font-semibold mb-6 text-gray-700">{t?.monthly_gcp_domain_revenue || 'GCP Revenue by Domain (€)'}</h2>
                        <div className="h-96">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={gcpMonthlyDomainStats} margin={{ top: 20, right: 10, left: 10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                    <XAxis dataKey="name" tick={{ fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                                    <YAxis tick={{ fill: '#6B7280' }} axisLine={false} tickLine={false} tickFormatter={(val) => `€${val}`} />
                                    <RechartsTooltip cursor={{ fill: '#F3F4F6' }} content={<CustomTooltip />} />
                                    <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ paddingLeft: '10px' }} />
                                    {topGcpDomains.map((domain, index) => (
                                        <Bar key={domain} dataKey={domain} fill={GCP_COLORS[index % GCP_COLORS.length]} radius={[4, 4, 0, 0]} onClick={(data) => handleChartClick(data, t?.monthly_gcp_domain_revenue || 'GCP Revenue by Domain')} className="cursor-pointer hover:opacity-80 transition-opacity" />
                                    ))}
                                    {gcpMonthlyDomainStats.some(s => (s.Other || 0) > 0) && (
                                        <Bar dataKey="Other" fill="#D1D5DB" radius={[4, 4, 0, 0]} onClick={(data) => handleChartClick(data, t?.monthly_gcp_domain_revenue || 'GCP Revenue by Domain')} className="cursor-pointer hover:opacity-80 transition-opacity" />
                                    )}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
            </div>

            {/* Remaining Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                {/* Top Customers Chart */}
                <div className="bg-white p-6 rounded-lg shadow-sm w-full">
                    <h2 className="text-xl font-semibold mb-6 text-gray-700">{t?.top_customers_revenue || 'Top Customers by Revenue (€)'}</h2>
                    <div className="h-96">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={customerPayments} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" />
                                <XAxis type="number" tick={{ fill: '#6B7280' }} axisLine={false} tickLine={false} tickFormatter={(val) => `€${val}`} />
                                <YAxis dataKey="name" type="category" tick={{ fill: '#374151', fontSize: 12 }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} width={250} tickFormatter={(val) => val && val.length > 40 ? `${val.substring(0, 40)}...` : val} />
                                <RechartsTooltip cursor={{ fill: '#F3F4F6' }} content={<CustomTooltip />} />
                                <Bar dataKey="value" fill="#673AB7" radius={[0, 4, 4, 0]} barSize={24} name={t?.revenue || 'Revenue'} onClick={(data) => handleChartClick(data, t?.customer_details || 'Customer Details')} className="cursor-pointer hover:opacity-80 transition-opacity" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Top Cart Items Chart */}
                <div className="bg-white p-6 rounded-lg shadow-sm w-full">
                    <h2 className="text-xl font-semibold mb-6 text-gray-700">{t?.top_cart_items_revenue || 'Top Cart Items by Revenue (€)'}</h2>
                    <div className="h-96">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={cartItemStats} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" />
                                <XAxis type="number" tick={{ fill: '#6B7280' }} axisLine={false} tickLine={false} tickFormatter={(val) => `€${val}`} />
                                <YAxis dataKey="name" type="category" tick={{ fill: '#374151', fontSize: 12 }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} width={250} tickFormatter={(val) => val && val.length > 40 ? `${val.substring(0, 40)}...` : val} />
                                <RechartsTooltip cursor={{ fill: '#F3F4F6' }} content={<CustomTooltip />} />
                                <Bar dataKey="revenue" fill="#E84A5F" radius={[0, 4, 4, 0]} barSize={24} name={t?.revenue || 'Revenue'} onClick={(data) => handleChartClick(data, t?.cart_items_details || 'Cart Item Details')} className="cursor-pointer hover:opacity-80 transition-opacity" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Top Flex Plan Items Chart */}
                <div className="bg-white p-6 rounded-lg shadow-sm w-full">
                    <h2 className="text-xl font-semibold mb-6 text-gray-700">{t?.top_flex_items_revenue || 'Top Flex Plan Items by Revenue (€)'}</h2>
                    <div className="h-96">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={flexItemStats} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" />
                                <XAxis type="number" tick={{ fill: '#6B7280' }} axisLine={false} tickLine={false} tickFormatter={(val) => `€${val}`} />
                                <YAxis dataKey="name" type="category" tick={{ fill: '#374151', fontSize: 12 }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} width={250} tickFormatter={(val) => val && val.length > 40 ? `${val.substring(0, 40)}...` : val} />
                                <RechartsTooltip cursor={{ fill: '#F3F4F6' }} content={<CustomTooltip />} />
                                <Bar dataKey="revenue" fill="#FFBB28" radius={[0, 4, 4, 0]} barSize={24} name={t?.revenue || 'Revenue'} onClick={(data) => handleChartClick(data, t?.flex_items_details || 'Flex Plan Item Details')} className="cursor-pointer hover:opacity-80 transition-opacity" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Payment Methods Chart */}
                <div className="bg-white p-6 rounded-lg shadow-sm w-full flex flex-col items-center">
                    <h2 className="text-xl font-semibold mb-6 text-gray-700 w-full text-left">{t?.payment_platforms || 'Payment Platforms (Orders)'}</h2>
                    <div className="h-80 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={paymentMethods}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={(props) => {
                                        const { cx, cy, midAngle, outerRadius, name, percent, index } = props;
                                        const RADIAN = Math.PI / 180;
                                        // Push the label slightly outside the pie
                                        const radius = outerRadius * 1.25;
                                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                        const y = cy + radius * Math.sin(-midAngle * RADIAN);

                                        const text = `${name} ${(percent * 100).toFixed(0)}%`;
                                        // Approximate text widths mapping
                                        const textWidth = text.length * 7;
                                        const hasLogo = ['paypal', 'stripe', 'card'].includes(name.toLowerCase());
                                        const logoWidth = 16;
                                        const logoGap = 6;
                                        const boxWidth = textWidth + 16;
                                        const boxHeight = 22;

                                        const isLeft = x < cx;
                                        const totalWidth = hasLogo ? boxWidth + logoWidth + logoGap : boxWidth;

                                        // Calculate positions based on whether label is on left or right of pie
                                        const logoX = isLeft ? x - totalWidth : x;
                                        const rectX = hasLogo ? logoX + logoWidth + logoGap : logoX;
                                        const rectY = y - boxHeight / 2;
                                        const textX = rectX + 8;

                                        return (
                                            <g>
                                                {hasLogo && (
                                                    <image
                                                        href={`/${name.toLowerCase()}-logo.png`}
                                                        x={logoX}
                                                        y={y - 8}
                                                        height={16}
                                                        width={16}
                                                    />
                                                )}
                                                <rect
                                                    x={rectX}
                                                    y={rectY}
                                                    width={boxWidth}
                                                    height={boxHeight}
                                                    fill={COLORS[index % COLORS.length]}
                                                    style={{ fill: COLORS[index % COLORS.length] }}
                                                    rx={4}
                                                    ry={4}
                                                />
                                                <text
                                                    x={textX}
                                                    y={y}
                                                    fill="#ffffff"
                                                    style={{ fill: "#ffffff" }}
                                                    textAnchor="start"
                                                    dominantBaseline="central"
                                                    fontSize={12}
                                                    fontWeight="bold"
                                                >
                                                    {text}
                                                </text>
                                            </g>
                                        );
                                    }}
                                    outerRadius={100}
                                    fill="#8884d8"
                                    dataKey="value"
                                    onClick={(data) => handleChartClick(data, t?.payment_platforms || 'Payment Platforms')}
                                    className="cursor-pointer hover:opacity-80 transition-opacity"
                                >
                                    {paymentMethods.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <RechartsTooltip content={<CustomTooltip />} />
                                <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
            </>)}

            {/* Modal for displaying detailed order data */}
            {
                isModalOpen && selectedData && createPortal(
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[100] flex items-center justify-center p-4">
                        <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xl font-semibold text-gray-800">{modalTitle} (<span className="text-primary">{selectedData.length}</span> {t?.admin_orders_dashboard || 'Orders'})</h3>
                                <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700 bg-gray-100 p-2 rounded-full focus:outline-none transition-colors">
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                                </button>
                            </div>
                            <div className="overflow-x-auto overflow-y-auto flex-grow rounded-lg border border-gray-200">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-primary sticky top-0 z-10 shadow-sm">
                                        <tr>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.date || 'Date'}</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.customer_name || 'Customer'}</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.order_user || 'Order User'}</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.ticket_key || 'Order ID'}</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.payment_details || 'Method'}</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.total_amount || 'Amount'}</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.status || 'Status'}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {selectedData.map((order, index) => (
                                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                    {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {order.customerName || order.customerDomain || order.debtorName || order.customerId || order.debtorId || 'Unknown'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {order.orderUser || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {order.id || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                                                    <div className="flex items-center gap-2">
                                                        {['paypal', 'stripe', 'card'].includes(order.paymentMethod?.toLowerCase()) && (
                                                            <img
                                                                src={`/${order.paymentMethod.toLowerCase()}-logo.png`}
                                                                alt={order.paymentMethod}
                                                                className="h-4 w-auto object-contain"
                                                            />
                                                        )}
                                                        <span>{order.paymentMethod || 'Unknown'}</span>
                                                        {(order.stripeReceiptUrl || order.receiptUrl) && (
                                                            <a
                                                                href={order.stripeReceiptUrl || order.receiptUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium text-indigo-600 hover:text-indigo-800 hover:underline bg-indigo-50 border border-indigo-100 rounded transition-colors shrink-0"
                                                                title={t?.view_receipt || "Bekijk betaalbewijs"}
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                </svg>
                                                                <span>{t?.receipt || 'Betaalbewijs'}</span>
                                                            </a>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800">
                                                    €{order.totalPrice ? order.totalPrice.toFixed(2) : '0.00'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${order.status?.toLowerCase().includes('success') || order.status?.toLowerCase().includes('completed') || order.status?.toLowerCase().includes('paid') ? 'bg-green-100 text-green-800 border border-green-200' : order.status?.toLowerCase().includes('pending') ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' : 'bg-red-100 text-red-800 border border-red-200'}`}>
                                                        {order.status || 'Unknown'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>,
                    document.body
                )
            }
        </div >
    );
};

export default AdminOrdersDashboard;
